import { useState, useCallback, useEffect, useRef } from 'react';
import { participantesService } from '../../../../services/Producto_3/Fort_Capacidades/CursosCiclosVida/cursoFortalecimientoPartiService';
import { message, Alert, Modal } from 'antd';
export function useParticipantes() {
  const [participantes, setParticipantes] = useState([]);
  const [tematicas, setTematicas] = useState([]);
  const [instancias, setInstancias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const funcionesRef = useRef({});

  const fetchParticipantes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await participantesService.getParticipantes();
      setParticipantes(result);
    } catch (err) {
      console.error("Error al obtener participantes:", err);
      setError(err.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTematicas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await participantesService.getTematicas();
      setTematicas(result);
    } catch (err) {
      console.error("Error al obtener temáticas:", err);
      setError(err.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInstancias = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await participantesService.getInstancias();
      setInstancias(result);
    } catch (err) {
      console.error("Error al obtener instancias:", err);
      setError(err.message || 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParticipantes();
    fetchTematicas();
    fetchInstancias();
  }, [fetchParticipantes, fetchTematicas, fetchInstancias]);


  const createParticipante = useCallback(async (participante) => {
    setLoading(true);
    setError(null);
    try {
      const result = await participantesService.createParticipante(participante);
      const newParticipante = { ...participante, oid: result.oid, children: [] };
      setParticipantes(prev => [...prev, newParticipante]);
      message.success('Participante creado con éxito');
      return result;
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error al crear participante';
      setError(errorMsg);
      message.error(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateParticipante = useCallback(async (oid, participante) => {
    setLoading(true);
    setError(null);
    try {
      await participantesService.updateParticipante(oid, participante);
      setParticipantes(prev =>
        prev.map(p => p.oid === oid ? { ...p, ...participante } : p)
      );
      message.success('Participante actualizado con éxito');
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error al actualizar participante';
      setError(errorMsg);
      message.error(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createAsistencia = useCallback(async (asistencia) => {
    setLoading(true);
    setError(null);
    try {
      const result = await participantesService.createAsistencia(asistencia);
      const newAsistencia = { ...asistencia, oid: result.oid };
      setParticipantes(prev =>
        prev.map(p =>
          p.oid === asistencia.oid_matriculado
            ? { ...p, children: [...(p.children || []), newAsistencia] }
            : p
        )
      );
      message.success('Asistencia registrada con éxito');
      return result;
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error al registrar asistencia';
      setError(errorMsg);
      message.error(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateAsistencia = useCallback(async (oid, asistencia) => {
    setLoading(true);
    setError(null);
    try {
      await participantesService.updateAsistencia(oid, asistencia);
      setParticipantes(prev => prev.map(p => {
        if (p.oid === asistencia.oid_matriculado) {
          return {
            ...p,
            children: p.children?.map(a =>
              a.oid === oid ? { ...a, ...asistencia } : a
            )
          };
        }
        return p;
      }));
      message.success('Asistencia actualizada con éxito');
      return true;
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error al actualizar asistencia';
      setError(errorMsg);
      message.error(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAsistencia = useCallback(async (oid, oid_matriculado) => {
    setLoading(true);
    setError(null);
    try {
      await participantesService.deleteAsistencia(oid);
      setParticipantes(prev =>
        prev.map(p =>
          p.oid === oid_matriculado
            ? { ...p, children: p.children?.filter(a => a.oid !== oid) }
            : p
        )
      );
      message.success('Asistencia eliminada con éxito');
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error al eliminar asistencia';
      setError(errorMsg);
      message.error(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

const deleteParticipante = useCallback(async (oid) => {
  setLoading(true);
  setError(null);
  try {
    const response = await participantesService.deleteParticipante(oid);
    
    // Verificar si la respuesta indica que hay asistencias
    if (response?.hasAsistencias) {
      return new Promise((resolve, reject) => {
        Modal.confirm({
          title: 'No se puede eliminar el participante',
          content: `Este participante tiene ${response.count} asistencias registradas. ¿Desea eliminar todas las asistencias y luego el participante?`,
          okText: 'Sí, eliminar todo',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              // Primero eliminar todas las asistencias
              const participante = participantes.find(p => p.oid === oid);
              if (participante?.children) {
                await Promise.all(
                  participante.children.map(asistencia => 
                    deleteAsistencia(asistencia.oid, oid)
                  )
                );
              }
              
              // Luego eliminar el participante
              await participantesService.forceDeleteParticipante(oid);
              setParticipantes(prev => prev.filter(p => p.oid !== oid));
              message.success('Participante y asistencias eliminados con éxito');
              resolve();
            } catch (err) {
              message.error('Error al eliminar asistencias: ' + (err.message || 'Error desconocido'));
              reject(err);
            }
          },
          onCancel: () => {
            resolve(); // Resolvemos sin hacer nada si el usuario cancela
          }
        });
      });
    }
    
    // Si no hay asistencias, simplemente eliminamos
    setParticipantes(prev => prev.filter(p => p.oid !== oid));
    message.success('Participante eliminado con éxito');
  } catch (err) {
    const errorMsg = err.response?.data?.message || err.message || 'Error al eliminar participante';
    setError(errorMsg);
    message.error(errorMsg);
    throw err;
  } finally {
    setLoading(false);
  }
}, [participantes, deleteAsistencia]);


  useEffect(() => {
    funcionesRef.current = {
      deleteAsistencia
    };
  }, [deleteAsistencia]);
  return {
    participantes,
    tematicas,
    instancias,
    loading,
    error,
    fetchParticipantes,
    createParticipante,
    updateParticipante,
    deleteParticipante,
    createAsistencia,
    updateAsistencia,
    deleteAsistencia
  };
}
